<template>
  <!-- Network status from the FNO's NMS (Jackal Creek BDCOM first). Hidden unless /nmsDashboard says available. Nov 2026. -->
  <div v-if="data && data.available" class="nmsd">
    <div class="nmsd-head">
      <div>
        <h2 class="nmsd-title">Network status</h2>
        <div class="nmsd-muted">
          <span v-if="data.mode === 'fno'">Live from the NMS, every ONU on {{ data.organisation }}'s network.</span>
          <span v-else>Your customers' ONTs, live from the network operator's NMS.</span>
          Updated {{ ago(data.updatedAt) }}.
        </div>
      </div>
      <v-btn small flat @click="load" :disabled="loading"><v-icon left small>refresh</v-icon>Refresh</v-btn>
    </div>

    <!-- outages first: the thing to act on -->
    <div v-if="data.outages.length" class="nmsd-card nmsd-alert">
      <h3><v-icon color="error" small>warning</v-icon> Area outages ({{ data.outages.length }})</h3>
      <div v-for="(o, i) in data.outages" :key="i" class="nmsd-outage">
        <span :class="['nmsd-pill', o.level]">{{ o.kind === 'pon-down' ? 'Whole fibre line down' : 'Many down together' }}</span>
        <span v-if="data.mode === 'fno'"><b>{{ o.site }}</b> · {{ o.olt }} · PON {{ o.pon }}: {{ o.offline }} of {{ o.total }} offline</span>
        <span v-else><b>{{ o.site }}</b>: {{ o.yourCustomers }} of your customers affected</span>
        <span class="nmsd-muted"> · since {{ ago(o.since) }}</span>
      </div>
      <div class="nmsd-muted nmsd-note" v-if="data.mode === 'fno'">
        Several ONUs on one PON down at the same time usually means a feeder/street fibre fault, a power cut in the area or the OLT port.
      </div>
      <div class="nmsd-muted nmsd-note" v-else>The network operator has been alerted by the same data; no need to log a fault per customer for these.</div>
    </div>

    <div class="nmsd-grid">
      <!-- ONU / customer totals -->
      <div class="nmsd-card">
        <h3>{{ data.mode === 'fno' ? 'ONUs' : 'Your customers' }}</h3>
        <div class="nmsd-rings">
          <div class="nmsd-ring">
            <svg viewBox="0 0 112 112"><circle class="bg" cx="56" cy="56" r="44"/>
              <circle class="fg ok" cx="56" cy="56" r="44" :stroke-dasharray="dash(pct(t.online, onlineBase))"/></svg>
            <span>{{ pct(t.online, onlineBase) }}%</span><small>online</small>
          </div>
          <div class="nmsd-big">
            <div><b class="ok">{{ t.online }}</b> online</div>
            <div><b class="bad">{{ t.offline }}</b> offline now</div>
            <div v-if="t.other"><b>{{ t.other }}</b> disabled / other</div>
            <div v-if="data.mode === 'fno'"><b>{{ t.total }}</b> ONUs on the NMS</div>
            <template v-else>
              <div><b>{{ t.accounts }}</b> live accounts</div>
              <div class="nmsd-muted" v-if="t.accounts - t.withOnt">{{ t.accounts - t.withOnt }} without an ONT linked</div>
              <div class="nmsd-muted" v-if="t.notOnNms">{{ t.notOnNms }} ONT{{ t.notOnNms === 1 ? '' : 's' }} not found on the NMS</div>
            </template>
          </div>
        </div>
        <div v-if="data.mode === 'fno'" class="nmsd-muted nmsd-note">
          {{ data.linked.onus }} ONUs linked to a portal account, {{ data.linked.notLinked }} not linked.
        </div>
      </div>

      <!-- Recently offline -->
      <div class="nmsd-card">
        <h3>Recently offline</h3>
        <div class="nmsd-mini">
          <div><b>{{ r.lastHour }}</b><span>Last hour</span><small>{{ r.cameBackHour }} came back</small></div>
          <div><b>{{ r.day }}</b><span>1–24 hours</span><small>dropped</small></div>
          <div><b>{{ r.overDay }}</b><span>Over a day</span><small>{{ r.overWeek }} over 7 days</small></div>
        </div>
        <div v-if="r.unknown" class="nmsd-muted nmsd-note">
          {{ r.unknown }} were already offline when tracking began{{ data.trackingSince ? ' (' + day(data.trackingSince) + ')' : '' }}, so their drop time isn't known.
        </div>
        <div class="nmsd-sub">Latest drops</div>
        <div v-if="!r.latest.length" class="nmsd-muted">No drops in the last 24 hours.</div>
        <div v-for="(d, i) in r.latest" :key="i" class="nmsd-row">
          <span class="nmsd-name">{{ who(d) }}</span>
          <span class="nmsd-muted nmsd-where">{{ data.mode === 'fno' ? d.olt : d.network }}</span>
          <span :class="d.stillOffline ? 'bad' : 'ok'">{{ ago(d.at) }}{{ d.stillOffline ? '' : ' · back' }}</span>
        </div>
      </div>

      <!-- FNO: sites / OLTs -->
      <div class="nmsd-card" v-if="data.mode === 'fno'">
        <h3>Sites and OLTs</h3>
        <table class="nmsd-tbl">
          <tr><th>OLT</th><th>PONs</th><th></th><th class="r">Online</th></tr>
          <tbody v-for="s in data.sites" :key="s.site">
            <tr class="nmsd-site"><td colspan="2">{{ s.site }}</td>
              <td><span class="nmsd-track"><i :class="tone(s)" :style="{width: pct(s.online, s.online + s.offline) + '%'}"></i></span></td>
              <td class="r"><b>{{ s.online }}</b> / {{ s.total }}</td></tr>
            <tr v-for="o in oltsOf(s.site)" :key="o.oltId">
              <td class="nmsd-indent">{{ o.name }}</td><td>{{ o.pons }}</td>
              <td><span class="nmsd-track"><i :class="tone(o)" :style="{width: pct(o.online, o.online + o.offline) + '%'}"></i></span></td>
              <td class="r">{{ o.online }} / {{ o.total }}</td></tr>
          </tbody>
        </table>
      </div>

      <!-- FNO: per ISP -->
      <div class="nmsd-card" v-if="data.mode === 'fno'">
        <h3>Customers per ISP</h3>
        <table class="nmsd-tbl">
          <tr><th>ISP</th><th class="r">Accounts</th><th class="r">With ONT</th><th class="r">Online</th><th class="r">Offline</th></tr>
          <tr v-for="i in data.isps" :key="i.isp">
            <td>{{ i.isp }}</td><td class="r">{{ i.accounts }}</td><td class="r">{{ i.withOnt }}</td>
            <td class="r ok">{{ i.online }}</td><td class="r" :class="{bad: i.offline}">{{ i.offline }}</td></tr>
        </table>
        <div class="nmsd-muted nmsd-note">From portal accounts. Accounts without a linked ONT can't show a status yet.</div>
      </div>
    </div>

    <!-- offline list -->
    <div class="nmsd-card">
      <div class="nmsd-listhead">
        <h3>Offline now ({{ data.offlineTotal }}){{ data.offlineTotal > data.offline.length ? ', newest ' + data.offline.length + ' shown' : '' }}</h3>
        <input v-model="search" class="nmsd-search" placeholder="Search unit, serial, circuit…" />
      </div>
      <v-data-table :headers="headers" :items="filtered" :rows-per-page-items="[10, 25, 100]" class="elevation-0">
        <template v-slot:items="props">
          <template v-if="data.mode === 'fno'">
            <td>{{ props.item.unit || props.item.label || '—' }}<div class="nmsd-muted" v-if="props.item.unit && props.item.label">{{ props.item.label }}</div></td>
            <td>{{ props.item.isps || 'Not linked' }}</td>
            <td>{{ props.item.serial }}</td>
            <td>{{ props.item.olt }} · {{ props.item.pon }}</td>
          </template>
          <template v-else>
            <td>{{ props.item.unit }}<div class="nmsd-muted">{{ props.item.network }}</div></td>
            <td>{{ props.item.customer || '—' }}<div class="nmsd-muted" v-if="props.item.mobile">{{ props.item.mobile }}</div></td>
            <td>{{ props.item.circuit }}</td>
            <td>{{ props.item.serial }}</td>
          </template>
          <td>{{ props.item.since ? ago(props.item.since) : 'before tracking began' }}</td>
        </template>
      </v-data-table>
    </div>
  </div>
  <div v-else-if="error" class="nmsd nmsd-muted">Network status: {{ error }}</div>
</template>

<script>
import Api from "@/services/Api";

export default {
  data() {
    return { data: null, loading: false, error: null, search: "", timer: null };
  },
  computed: {
    t() { return this.data.totals; },
    r() { return this.data.recent; },
    onlineBase() { return this.t.online + this.t.offline; },
    headers() {
      const h = this.data.mode === "fno"
        ? [{ text: "Unit / ONU", value: "unit" }, { text: "ISP", value: "isps" }, { text: "Serial", value: "serial" }, { text: "OLT · PON", value: "olt" }]
        : [{ text: "Unit", value: "unit" }, { text: "Customer", value: "customer" }, { text: "Circuit", value: "circuit" }, { text: "ONT serial", value: "serial" }];
      return h.map(x => ({ ...x, sortable: false })).concat([{ text: "Offline since", value: "since", sortable: false }]);
    },
    filtered() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.data.offline;
      return this.data.offline.filter(x => Object.values(x).some(v => v != null && String(v).toLowerCase().includes(q)));
    }
  },
  mounted() {
    this.load();
    this.timer = setInterval(this.load, 5 * 60 * 1000);
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await Api().get("/nmsDashboard");
        this.data = res.data;
        this.error = null;
      } catch (e) {
        // Only show an error if the card was showing before; otherwise stay hidden (e.g. orgs without an NMS).
        if (this.data && this.data.available) this.error = "could not refresh";
      } finally {
        this.loading = false;
      }
    },
    pct(a, b) { return b ? Math.round((1000 * a) / b) / 10 : 0; },
    dash(p) { const c = 2 * Math.PI * 44; return (p / 100) * c + " " + c; },
    tone(x) { const h = this.pct(x.online, x.online + x.offline); return h >= 95 ? "ok" : h >= 85 ? "warn" : "bad"; },
    oltsOf(site) { return this.data.olts.filter(o => (o.site || "Unassigned") === site); },
    who(d) { return this.data.mode === "fno" ? (d.unit || d.label || d.serial) : (d.unit || d.circuit || d.serial); },
    day(t) { return new Date(t).toLocaleDateString("en-ZA", { day: "numeric", month: "short" }); },
    ago(t) {
      if (!t) return "unknown";
      const m = Math.round((Date.now() - new Date(t).getTime()) / 60000);
      if (m < 1) return "just now";
      if (m < 60) return m + " min ago";
      if (m < 1440) return Math.round(m / 60) + " h ago";
      return Math.round(m / 1440) + " d ago";
    }
  }
};
</script>

<style scoped>
.nmsd { margin: 16px; }
.nmsd-head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px; flex-wrap: wrap; gap: 8px; }
.nmsd-title { font-size: 22px; font-weight: 500; margin: 0; }
.nmsd-muted { color: #757575; font-size: 13px; }
.nmsd-note { margin-top: 10px; }
.nmsd-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(340px, 1fr)); gap: 14px; margin-bottom: 14px; }
.nmsd-card { background: #fff; border-radius: 4px; padding: 14px 16px; box-shadow: 0 1px 3px rgba(0,0,0,.15); margin-bottom: 14px; }
.nmsd-grid .nmsd-card { margin-bottom: 0; }
.nmsd-card h3 { font-size: 16px; font-weight: 500; margin: 0 0 10px; }
.nmsd-alert { border-left: 4px solid #e53935; }
.nmsd-outage { padding: 4px 0; font-size: 14px; }
.nmsd-pill { display: inline-block; font-size: 12px; padding: 1px 8px; border-radius: 10px; margin-right: 6px; color: #fff; }
.nmsd-pill.bad { background: #e53935; }
.nmsd-pill.warn { background: #fb8c00; }
.nmsd-rings { display: flex; align-items: center; gap: 20px; }
.nmsd-ring { position: relative; width: 112px; height: 112px; flex: none; text-align: center; }
.nmsd-ring svg { width: 112px; height: 112px; transform: rotate(-90deg); }
.nmsd-ring circle { fill: none; stroke-width: 12; }
.nmsd-ring .bg { stroke: #eee; }
.nmsd-ring .fg.ok { stroke: #43a047; }
.nmsd-ring span { position: absolute; left: 0; right: 0; top: 36px; font-size: 22px; font-weight: 500; }
.nmsd-ring small { position: absolute; left: 0; right: 0; top: 64px; color: #757575; }
.nmsd-big div { font-size: 14px; margin: 3px 0; }
.nmsd-big b { font-size: 18px; margin-right: 4px; }
.nmsd-mini { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; text-align: center; }
.nmsd-mini b { display: block; font-size: 22px; font-weight: 500; }
.nmsd-mini span { display: block; font-size: 13px; }
.nmsd-mini small { color: #757575; }
.nmsd-sub { font-size: 12px; text-transform: uppercase; letter-spacing: .04em; color: #757575; margin: 12px 0 4px; }
.nmsd-row { display: flex; gap: 8px; font-size: 13px; padding: 3px 0; border-bottom: 1px solid #f2f2f2; }
.nmsd-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.nmsd-where { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.nmsd-tbl { width: 100%; border-collapse: collapse; font-size: 13px; }
.nmsd-tbl th { text-align: left; color: #757575; font-weight: 500; padding: 4px 6px; border-bottom: 1px solid #e0e0e0; }
.nmsd-tbl td { padding: 4px 6px; border-bottom: 1px solid #f2f2f2; }
.nmsd-tbl .r { text-align: right; }
.nmsd-site td { font-weight: 500; background: #fafafa; }
.nmsd-indent { padding-left: 18px !important; }
.nmsd-track { display: inline-block; width: 100%; min-width: 60px; height: 8px; background: #eee; border-radius: 4px; overflow: hidden; vertical-align: middle; }
.nmsd-track i { display: block; height: 100%; }
.nmsd-track i.ok { background: #43a047; }
.nmsd-track i.warn { background: #fb8c00; }
.nmsd-track i.bad { background: #e53935; }
.ok { color: #2e7d32; }
.bad { color: #c62828; }
.nmsd-listhead { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px; }
.nmsd-search { border: 1px solid #ccc; border-radius: 4px; padding: 5px 8px; font-size: 13px; min-width: 220px; }
</style>
